(() => {
  "use strict";

  const SELECTORS = {
    header: "[data-header]",
    desktopItem: ".nav__item[data-has-dropdown]",
    desktopTrigger: "[data-nav-trigger]",
    desktopPanel: "[data-nav-panel]",
    searchToggle: "[data-search-toggle]",
    searchPanel: "[data-search-panel]",
    cartToggle: "[data-cart-toggle]",
    mobileToggle: "[data-mobile-menu-toggle]",
    mobileDrawer: "[data-mobile-drawer]",
    mobileSubTrigger: "[data-mobile-submenu-trigger]",
    navDropdownContainer: ".js-nav-dropdown",
  };

  const DESKTOP_MQ = "(min-width: 1180px)";
  const isDesktop = () => window.matchMedia(DESKTOP_MQ).matches;
  const isMobile = () => !isDesktop();

  // -----------------------------
  // Focus helpers
  // -----------------------------
  const FOCUSABLE = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");

  const isActuallyFocusable = (el) => {
    if (!el) return false;
    if (el.closest("[hidden]")) return false;
    const rects = el.getClientRects();
    return !!rects && rects.length > 0;
  };

  const getFocusable = (container) => {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter(isActuallyFocusable);
  };

  const getPanelForTrigger = (trigger) => {
    const id = trigger?.getAttribute("aria-controls");
    return id ? document.getElementById(id) : null;
  };

  const getAllDesktopTriggersInHeader = (headerEl) => {
    return Array.from(headerEl.querySelectorAll(SELECTORS.desktopTrigger)).filter(isActuallyFocusable);
  };

  const focusNextTopTriggerOrNextHeaderFocusable = (headerEl, fromTrigger) => {
    const triggers = getAllDesktopTriggersInHeader(headerEl);
    const triggerIndex = triggers.indexOf(fromTrigger);

    if (triggerIndex > -1 && triggers[triggerIndex + 1]) {
      triggers[triggerIndex + 1].focus();
      return;
    }

    const headerFocusables = getFocusable(headerEl);
    const headerIndex = headerFocusables.indexOf(fromTrigger);

    if (headerIndex > -1 && headerFocusables[headerIndex + 1]) {
      headerFocusables[headerIndex + 1].focus();
    }
  };

  const getHeaderFocusables = (headerEl) => getFocusable(headerEl);

  const getNextFocusableInHeader = (headerEl, fromEl) => {
    const all = getHeaderFocusables(headerEl);
    const index = all.indexOf(fromEl);
    return index > -1 ? all[index + 1] : null;
  };

  // -----------------------------
  // Search helpers
  // -----------------------------
  const openSearch = (searchPanel, searchToggle) => {
    searchPanel.classList.add("is-open");
    searchToggle?.setAttribute("aria-expanded", "true");
  };

  const closeSearch = (searchPanel, searchToggle) => {
    searchPanel.classList.remove("is-open");
    searchToggle?.setAttribute("aria-expanded", "false");
  };

  const isSearchOpen = (searchPanel) => searchPanel.classList.contains("is-open");

  const init = () => {
    const header = document.querySelector(SELECTORS.header);
    if (!header) return;

    const searchPanel = header.querySelector(SELECTORS.searchPanel);
    const searchToggle = header.querySelector(SELECTORS.searchToggle);
    const navDropdownContainer = header.querySelector(SELECTORS.navDropdownContainer);

    // -----------------------------
    // Cart events (drawer contract)
    // -----------------------------
    const closeCart = () => {
      document.dispatchEvent(new CustomEvent("cart:close", { bubbles: true }));
    };

    // -----------------------------
    // Desktop nav state
    // -----------------------------
    const HOVER_INTENT = Object.freeze({
      OPEN_DELAY: 80,
      SWITCH_DELAY: 300,
      CLOSE_DELAY: 250,
    });

    let activeItem = null;
    let openTimer = null;
    let switchTimer = null;
    let closeTimer = null;

    const clearDesktopTimers = () => {
      if (openTimer) window.clearTimeout(openTimer);
      if (switchTimer) window.clearTimeout(switchTimer);
      if (closeTimer) window.clearTimeout(closeTimer);

      openTimer = null;
      switchTimer = null;
      closeTimer = null;
    };

    const cancelDesktopClose = () => {
      if (closeTimer) window.clearTimeout(closeTimer);
      closeTimer = null;
    };

    const isFocusWithinDesktopNav = () => {
      const focused = document.activeElement;
      if (!focused) return false;

      return header.contains(focused) || navDropdownContainer?.contains(focused);
    };

    const isPointerWithinDesktopNav = () => {
      if (!isDesktop()) return false;

      const hoveredTopItem = header.querySelector(`${SELECTORS.desktopItem}:hover`);
      const hoveredDropdown = navDropdownContainer?.matches(":hover");

      return !!hoveredTopItem || !!hoveredDropdown;
    };

    const shouldKeepDesktopNavOpen = () => {
      if (!isDesktop()) return false;
      return isFocusWithinDesktopNav() || isPointerWithinDesktopNav();
    };

    const activateDesktopItem = (item) => {
      if (!isDesktop() || !item || !navDropdownContainer) return;

      const trigger = item.querySelector(SELECTORS.desktopTrigger);
      const panelId = trigger?.getAttribute("aria-controls");
      if (!trigger || !panelId) return;

      header.querySelectorAll(SELECTORS.desktopItem).forEach((otherItem) => {
        const otherTrigger = otherItem.querySelector(SELECTORS.desktopTrigger);
        const isActive = otherItem === item;

        otherItem.classList.toggle("is-open", isActive);
        if (otherTrigger) {
          otherTrigger.setAttribute("aria-expanded", isActive ? "true" : "false");
        }
      });

      navDropdownContainer.classList.add("is-open");

      navDropdownContainer.querySelectorAll(SELECTORS.desktopPanel).forEach((panel) => {
        const isActive = panel.id === panelId;
        panel.classList.toggle("is-active", isActive);
        panel.setAttribute("aria-hidden", isActive ? "false" : "true");
      });

      activeItem = item;
    };

    const deactivateDesktopNav = () => {
      header.querySelectorAll(SELECTORS.desktopItem).forEach((item) => {
        item.classList.remove("is-open");

        const trigger = item.querySelector(SELECTORS.desktopTrigger);
        if (trigger) trigger.setAttribute("aria-expanded", "false");
      });

      if (navDropdownContainer) {
        navDropdownContainer.classList.remove("is-open");

        navDropdownContainer.querySelectorAll(SELECTORS.desktopPanel).forEach((panel) => {
          panel.classList.remove("is-active");
          panel.setAttribute("aria-hidden", "true");
        });
      }

      activeItem = null;
    };

    const scheduleDesktopClose = () => {
      if (!isDesktop()) return;

      cancelDesktopClose();

      closeTimer = window.setTimeout(() => {
        if (shouldKeepDesktopNavOpen()) return;
        deactivateDesktopNav();
      }, HOVER_INTENT.CLOSE_DELAY);
    };

    const scheduleActivateDesktopItem = (item, { immediate = false } = {}) => {
      if (!isDesktop() || !item) return;
      if (activeItem === item) return;

      if (openTimer) window.clearTimeout(openTimer);
      if (switchTimer) window.clearTimeout(switchTimer);
      openTimer = null;
      switchTimer = null;

      const run = () => {
        closeCart();
        if (searchPanel && searchToggle) closeSearch(searchPanel, searchToggle);
        activateDesktopItem(item);
      };

      if (immediate) {
        run();
        return;
      }

      const delay = activeItem ? HOVER_INTENT.SWITCH_DELAY : HOVER_INTENT.OPEN_DELAY;

      if (activeItem) {
        switchTimer = window.setTimeout(run, delay);
      } else {
        openTimer = window.setTimeout(run, delay);
      }
    };

    // -----------------------------
    // Close helpers
    // -----------------------------
    let closeMobileUI = () => {};

    const closeHeaderUI = () => {
      deactivateDesktopNav();
      if (searchPanel && searchToggle) closeSearch(searchPanel, searchToggle);
    };

    document.addEventListener("cart:open", () => {
      closeHeaderUI();
      closeMobileUI();
    });

    // -----------------------------
    // Desktop mega menu events
    // -----------------------------
    header.querySelectorAll(SELECTORS.desktopItem).forEach((item) => {
      const trigger = item.querySelector(SELECTORS.desktopTrigger);
      if (!trigger) return;

      item.addEventListener("pointerenter", () => {
        if (!isDesktop()) return;
        cancelDesktopClose();
        scheduleActivateDesktopItem(item);
      });

      item.addEventListener("pointerleave", () => {
        if (!isDesktop()) return;
        scheduleDesktopClose();
      });

      item.addEventListener("focusin", () => {
        if (!isDesktop()) return;
        cancelDesktopClose();
        scheduleActivateDesktopItem(item, { immediate: true });
      });

      item.addEventListener("focusout", () => {
        if (!isDesktop()) return;
        scheduleDesktopClose();
      });

      trigger.addEventListener("click", (e) => {
        if (isDesktop()) return;

        e.preventDefault();
        e.stopPropagation();

        const isOpen = item.classList.contains("is-open");
        closeHeaderUI();

        if (!isOpen) {
          activeItem = item;
          activateDesktopItem(item);
        }
      });

      trigger.addEventListener("keydown", (e) => {
        if (!isDesktop()) return;

        if (e.key === "Escape") {
          e.preventDefault();
          deactivateDesktopNav();
          trigger.focus();
          return;
        }

        if (e.key === "Tab" && !e.shiftKey) {
          const isOpen = item.classList.contains("is-open");
          if (!isOpen) return;

          const panel = getPanelForTrigger(trigger);
          const focusables = getFocusable(panel);

          if (focusables.length) {
            e.preventDefault();
            focusables[0].focus();
          } else {
            e.preventDefault();
            focusNextTopTriggerOrNextHeaderFocusable(header, trigger);
          }
        }
      });
    });

    if (navDropdownContainer) {
      navDropdownContainer.addEventListener("pointerenter", () => {
        if (!isDesktop()) return;
        cancelDesktopClose();

        if (switchTimer) {
          window.clearTimeout(switchTimer);
          switchTimer = null;
        }
      });

      navDropdownContainer.addEventListener("pointerleave", () => {
        if (!isDesktop()) return;
        scheduleDesktopClose();
      });

      navDropdownContainer.addEventListener("focusin", () => {
        if (!isDesktop()) return;
        cancelDesktopClose();
      });

      navDropdownContainer.addEventListener("focusout", () => {
        if (!isDesktop()) return;
        scheduleDesktopClose();
      });

      navDropdownContainer.addEventListener("keydown", (e) => {
        if (!isDesktop() || e.key !== "Tab") return;

        const focused = document.activeElement;
        if (!focused || !navDropdownContainer.contains(focused)) return;

        const activePanel = navDropdownContainer.querySelector(`${SELECTORS.desktopPanel}.is-active`);
        if (!activePanel) return;

        const focusables = getFocusable(activePanel);
        if (!focusables.length) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const expandedTrigger = header.querySelector(`${SELECTORS.desktopTrigger}[aria-expanded="true"]`);

        if (e.shiftKey) {
          if (focused === first) {
            e.preventDefault();
            expandedTrigger?.focus();
          }
          return;
        }

        if (focused === last) {
          e.preventDefault();

          const nextEl = expandedTrigger ? getNextFocusableInHeader(header, expandedTrigger) : null;
          const nextIsDropdownTrigger = !!(nextEl && nextEl.matches(SELECTORS.desktopTrigger));

          if (!nextIsDropdownTrigger) {
            deactivateDesktopNav();
          }

          if (nextEl) {
            nextEl.focus();
          } else {
            deactivateDesktopNav();
          }
        }
      });
    }

    // -----------------------------
    // Search panel toggle (desktop)
    // -----------------------------
    if (searchToggle && searchPanel) {
      closeSearch(searchPanel, searchToggle);

      searchToggle.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        deactivateDesktopNav();
        closeCart();

        if (isSearchOpen(searchPanel)) {
          closeSearch(searchPanel, searchToggle);
          return;
        }

        openSearch(searchPanel, searchToggle);

        const input = searchPanel.querySelector('input[type="search"]');
        if (input) input.focus();
      });

      document.addEventListener("click", (e) => {
        if (!isSearchOpen(searchPanel)) return;
        if (searchPanel.contains(e.target) || searchToggle.contains(e.target)) return;
        closeSearch(searchPanel, searchToggle);
      });
    }

    // -----------------------------
    // Mobile drawer + animated open/close
    // -----------------------------
    const mobileToggle = header.querySelector(SELECTORS.mobileToggle);
    const mobileDrawer = header.querySelector(SELECTORS.mobileDrawer);
    const hasMobileDrawer = !!(mobileToggle && mobileDrawer);

    let closeAllMobile = () => {};
    let closeMobileDrawer = () => {};

    if (hasMobileDrawer) {
      const getMobilePanel = (btn) => {
        const id = btn.getAttribute("aria-controls");
        return id ? document.getElementById(id) : null;
      };

      const openMobilePanel = (panel) => {
        if (!panel) return;

        panel.hidden = false;
        panel.classList.add("is-open");
        panel.style.height = "0px";

        const targetHeight = panel.scrollHeight;

        panel.offsetHeight;
        panel.style.height = `${targetHeight}px`;

        const onEnd = (e) => {
          if (e.propertyName !== "height") return;
          panel.style.height = "auto";
          panel.removeEventListener("transitionend", onEnd);
        };

        panel.addEventListener("transitionend", onEnd);
      };

      const closeMobilePanel = (panel) => {
        if (!panel) return;

        panel.classList.remove("is-open");

        const startHeight = panel.scrollHeight;
        panel.style.height = `${startHeight}px`;

        panel.offsetHeight;
        panel.style.height = "0px";

        const onEnd = (e) => {
          if (e.propertyName !== "height") return;
          panel.hidden = true;
          panel.removeEventListener("transitionend", onEnd);
        };

        panel.addEventListener("transitionend", onEnd);
      };

      closeAllMobile = (exceptBtn = null) => {
        mobileDrawer.querySelectorAll(SELECTORS.mobileSubTrigger).forEach((btn) => {
          if (exceptBtn && btn === exceptBtn) return;

          const panel = getMobilePanel(btn);
          btn.setAttribute("aria-expanded", "false");

          if (panel && !panel.hidden) {
            closeMobilePanel(panel);
          }
        });
      };

      const openMobileDrawer = () => {
        document.body.classList.add("no-scroll");

        mobileDrawer.hidden = false;
        window.requestAnimationFrame(() => {
          mobileDrawer.classList.add("is-open");
        });
      };

      closeMobileDrawer = () => {
        mobileDrawer.classList.remove("is-open");

        const finish = (e) => {
          if (e && e.target !== mobileDrawer) return;

          document.body.classList.remove("no-scroll");
          mobileDrawer.hidden = true;
          mobileDrawer.removeEventListener("transitionend", finish);
        };

        mobileDrawer.addEventListener("transitionend", finish);
      };

      closeMobileUI = () => {
        if (mobileDrawer.hidden) return;
        closeAllMobile(null);
        closeMobileDrawer();
      };

      mobileToggle.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        closeHeaderUI();

        const willOpen = mobileDrawer.hidden;

        if (willOpen) {
          if (isMobile()) closeCart();

          mobileToggle.classList.add("is-open");
          mobileToggle.setAttribute("aria-expanded", "true");

          openMobileDrawer();

          const firstFocusable = mobileDrawer.querySelector("a, button, input, select, textarea");
          if (firstFocusable) firstFocusable.focus();
        } else {
          mobileToggle.classList.remove("is-open");
          mobileToggle.setAttribute("aria-expanded", "false");

          closeMobileUI();
          mobileToggle.focus();
        }
      });

      mobileDrawer.addEventListener("click", (e) => {
        if (e.target !== mobileDrawer) return;
        closeMobileUI();
        mobileToggle.focus();
      });

      mobileDrawer.querySelectorAll(SELECTORS.mobileSubTrigger).forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();

          const panel = getMobilePanel(btn);
          if (!panel) return;

          const expanded = btn.getAttribute("aria-expanded") === "true";

          if (expanded) {
            btn.setAttribute("aria-expanded", "false");
            if (!panel.hidden) closeMobilePanel(panel);
          } else {
            closeAllMobile(btn);
            btn.setAttribute("aria-expanded", "true");
            if (panel.hidden) openMobilePanel(panel);
          }
        });
      });
    }

    // -----------------------------
    // Cart toggle (drawer hook)
    // -----------------------------
    const cartToggle = header.querySelector(SELECTORS.cartToggle);
    if (cartToggle) {
      cartToggle.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        closeHeaderUI();

        if (isMobile() && hasMobileDrawer) {
          closeMobileUI();
        }
      });
    }

    // -----------------------------
    // Escape closes everything
    // -----------------------------
    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;

      closeHeaderUI();

      if (hasMobileDrawer && !mobileDrawer.hidden) {
        closeMobileUI();
        mobileToggle.focus();
      }
    });

    // -----------------------------
    // Resize guard
    // -----------------------------
    window.addEventListener("resize", () => {
      clearDesktopTimers();

      if (!isDesktop()) {
        deactivateDesktopNav();
      }
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
